import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';

import { analyticsService } from './services/analyticsService';
// --- Constants & Config ---
const BASE_URL = import.meta.env.DEV ? '/api' : 'https://christopherzw7zyi.lastapp.dev';
const APP_ID = '942f5d1f-440d-45a8-80a1-53d6ab964e6a';
const AI_UID_TTS = 'c768e003adc70e6c5c0ee2380a586f2749ff83bdbcedb0038069ecae43f5d033';

const getFullImageUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BASE_URL.replace('/api', 'https://christopherzw7zyi.lastapp.dev')}${url}`;
  return `${BASE_URL.replace('/api', 'https://christopherzw7zyi.lastapp.dev')}/${url}`;
};

// --- Types ---
interface User {
  id: string;
  email: string;
  provider: string;
}

interface Story {
  id: string;
  user_id: string;
  title: string;
  description: string;
  cover_image_url: string;
  category: string;
  language: string;
  is_published: boolean;
  is_premium: boolean;
  price: number;
  total_episodes: number;
  total_duration_seconds: number;
  tags: string[];
  metadata: any;
}

interface Episode {
  id: string;
  story_id: string;
  episode_number: number;
  title: string;
  description: string;
  audio_url: string;
  duration_seconds: number;
  is_published: boolean;
  is_premium: boolean;
  transcript: string;
  metadata: any;
}

// --- Contexts ---
interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
}
const AuthContext = createContext<AuthContextType>({ user: null, login: () => {}, logout: () => {} });

interface PlayerContextType {
  currentEpisode: Episode | null;
  currentStory: Story | null;
  isPlaying: boolean;
  playEpisode: (episode: Episode, story: Story) => void;
  togglePlay: () => void;
  closePlayer: () => void;
}
const PlayerContext = createContext<PlayerContextType>({
  currentEpisode: null,
  currentStory: null,
  isPlaying: false,
  playEpisode: () => {},
  togglePlay: () => {},
  closePlayer: () => {}
});

// --- API Helpers ---
const apiFetch = async (endpoint: string, method: string = 'GET', body?: any) => {
  const options: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body) options.body = JSON.stringify(body);
  
  const response = await fetch(`${BASE_URL}${endpoint}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'API Error');
  }
  return response.json();
};

const uploadFile = async (file: File, userId: string): Promise<string> => {
  const formData = new FormData();
  formData.append('app_id', APP_ID);
  formData.append('user_id', userId);
  formData.append('file', file);

  const response = await fetch(`${BASE_URL}/data/upload`, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) throw new Error('Upload failed');
  const data = await response.json();
  return data.url;
};

const generateTTS = async (prompt: string): Promise<string> => {
  const formData = new FormData();
  formData.append('app_id', APP_ID);
  formData.append('uid', AI_UID_TTS);
  formData.append('prompt', prompt);

  const response = await fetch(`${BASE_URL}/aiapi/audio`, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) throw new Error('TTS Generation failed');
  const data = await response.json();
  return data.result;
};

// --- Components ---

const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg animate-slide-up text-white ${type === 'success' ? 'bg-success dark:bg-green-600' : 'bg-error dark:bg-red-600'}`}>
      <div className="flex items-center gap-2">
        <i className={`fa ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
        <span>{message}</span>
      </div>
    </div>
  );
};

const GlobalPlayer = () => {
  const { currentEpisode, currentStory, isPlaying, togglePlay, closePlayer } = useContext(PlayerContext);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) audioRef.current.play().catch(e => console.error("Playback failed", e));
      else audioRef.current.pause();
    }
  }, [isPlaying, currentEpisode]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };

  if (!currentEpisode || !currentStory) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-surface dark:bg-gray-800 border-t border-border dark:border-gray-700 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-50 animate-slide-up">
      <div className="h-1 bg-muted dark:bg-gray-700 w-full">
        <div className="h-full bg-primary dark:bg-primary-500 transition-all duration-200" style={{ width: `${progress || 0}%` }}></div>
      </div>
      <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <img src={getFullImageUrl(currentStory.cover_image_url)} alt="Cover" className="w-12 h-12 rounded-md object-cover shadow-sm" />
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-foreground dark:text-white truncate">{currentEpisode.title}</h4>
            <p className="text-xs text-muted-foreground dark:text-gray-400 truncate">{currentStory.title}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-primary dark:bg-primary-500 text-white flex items-center justify-center hover:scale-105 transition-transform shadow-md">
            <i className={`fa ${isPlaying ? 'fa-pause' : 'fa-play'} ml-${isPlaying ? '0' : '1'}`}></i>
          </button>
          <button onClick={closePlayer} className="w-8 h-8 text-muted-foreground dark:text-gray-400 hover:text-foreground dark:hover:text-white transition-colors">
            <i className="fa fa-times"></i>
          </button>
        </div>
      </div>
      <audio 
        ref={audioRef} 
        src={getFullImageUrl(currentEpisode.audio_url)} 
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => togglePlay()}
      />
    </div>
  );
};

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { path: '/', icon: 'fa-home', label: 'Discover' },
    { path: '/library', icon: 'fa-bookmark', label: 'Library' },
    { path: '/creator', icon: 'fa-microphone', label: 'Studio' },
  ];

  return (
    <>
      {/* Desktop Sidebar / Top Nav */}
      <nav className="hidden md:flex sticky top-0 z-40 w-full bg-surface/90 dark:bg-gray-800/90 backdrop-blur-md border-b border-border dark:border-gray-700 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 w-full h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-primary dark:text-primary-500 font-bold text-xl">
            <i className="fa fa-headphones-alt"></i>
            <span>StoryCrafter</span>
          </Link>
          
          <div className="flex items-center gap-6">
            {navLinks.map(link => (
              <Link 
                key={link.path} 
                to={link.path}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${location.pathname === link.path ? 'text-primary dark:text-primary-400' : 'text-muted-foreground dark:text-gray-400 hover:text-foreground dark:hover:text-white'}`}
              >
                <i className={`fa ${link.icon}`}></i>
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground dark:text-gray-300">{user.email}</span>
                <button onClick={() => { logout(); navigate('/login'); }} className="text-sm font-medium text-error dark:text-red-400 hover:underline">
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/login" className="bg-gradient-warm text-white px-4 py-2 rounded-button text-sm font-bold shadow-md hover:shadow-lg transition-all">
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-surface dark:bg-gray-800 border-t border-border dark:border-gray-700 pb-safe">
        <div className="flex items-center justify-around h-16 px-2">
          {navLinks.map(link => (
            <Link 
              key={link.path} 
              to={link.path}
              className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${location.pathname === link.path ? 'text-primary dark:text-primary-400' : 'text-muted-foreground dark:text-gray-400'}`}
            >
              <i className={`fa ${link.icon} text-lg`}></i>
              <span className="text-[10px] font-medium">{link.label}</span>
            </Link>
          ))}
          {user ? (
            <button onClick={() => { logout(); navigate('/login'); }} className="flex flex-col items-center justify-center w-full h-full space-y-1 text-muted-foreground dark:text-gray-400">
              <i className="fa fa-sign-out-alt text-lg"></i>
              <span className="text-[10px] font-medium">Logout</span>
            </button>
          ) : (
            <Link to="/login" className="flex flex-col items-center justify-center w-full h-full space-y-1 text-muted-foreground dark:text-gray-400">
              <i className="fa fa-user text-lg"></i>
              <span className="text-[10px] font-medium">Login</span>
            </Link>
          )}
        </div>
      </nav>
    </>
  );
};

// --- Screens ---

const AuthScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const validatePassword = (pass: string) => {
    const minLength = pass.length >= 6;
    const hasCapital = /[A-Z]/.test(pass);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    return minLength && hasCapital && hasSpecial;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isLogin && !validatePassword(password)) {
      setError('Password must contain at least 6 characters, 1 capital letter, and 1 special character.');
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        const res = await apiFetch('/data/login', 'POST', {
          app_id: APP_ID,
          email,
          password,
          provider: 'email'
        });
        login(res);
        navigate('/');
      } else {
        const res = await apiFetch('/data', 'POST', {
          app_id: APP_ID,
          table_name: 'users',
          data: { email, password, provider: 'email' }
        });
        login(res);
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background dark:bg-gray-900 p-4">
      <div className="w-full max-w-md bg-surface dark:bg-gray-800 rounded-card shadow-xl border border-border dark:border-gray-700 p-8 animate-fade-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-warm rounded-full flex items-center justify-center mx-auto mb-4 shadow-glow">
            <i className="fa fa-headphones-alt text-2xl text-white"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground dark:text-white">
            {isLogin ? 'Welcome Back' : 'Join StoryCrafter'}
          </h1>
          <p className="text-muted-foreground dark:text-gray-400 mt-2">
            {isLogin ? 'Sign in to continue listening' : 'Create an account to start your journey'}
          </p>
        </div>

        {error && (
          <div className="bg-error/10 dark:bg-red-900/30 border border-error/20 dark:border-red-800 text-error dark:text-red-400 p-3 rounded-lg text-sm mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Email</label>
            <input 
              type="email" 
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 focus:border-transparent outline-none transition-all dark:text-white"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Password</label>
            <input 
              type="password" 
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 focus:border-transparent outline-none transition-all dark:text-white"
              placeholder="••••••••"
            />
            {!isLogin && (
              <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2">
                Password must contain: At least 6 characters, 1 capital letter, and 1 special character.
              </p>
            )}
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full h-12 bg-gradient-warm text-white rounded-button font-bold shadow-md hover:shadow-lg active:scale-[0.98] transition-all disabled:opacity-70 flex items-center justify-center"
          >
            {loading ? <i className="fa fa-spinner fa-spin"></i> : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button 
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-primary dark:text-primary-400 font-medium hover:underline"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
};

const HomeScreen = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await apiFetch(`/data?app_id=${APP_ID}&table_name=stories`);
        setStories(res.filter((s: Story) => s.is_published));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 animate-fade-in pb-24 md:pb-8">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground dark:text-white mb-2">Discover Stories</h1>
        <p className="text-muted-foreground dark:text-gray-400">Immerse yourself in captivating audio experiences.</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-surface dark:bg-gray-800 rounded-card h-64 animate-pulse border border-border dark:border-gray-700"></div>
          ))}
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-20 bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700">
          <i className="fa fa-book-open text-4xl text-muted-foreground dark:text-gray-500 mb-4"></i>
          <h3 className="text-xl font-bold text-foreground dark:text-white">No stories found</h3>
          <p className="text-muted-foreground dark:text-gray-400 mt-2">Check back later for new content.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {stories.map(story => (
            <Link to={`/story/${story.id}`} key={story.id} className="group bg-surface dark:bg-gray-800 rounded-card overflow-hidden border border-border dark:border-gray-700 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col">
              <div className="aspect-square relative overflow-hidden bg-muted dark:bg-gray-700">
                {story.cover_image_url ? (
                  <img src={getFullImageUrl(story.cover_image_url)} alt={story.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground dark:text-gray-500">
                    <i className="fa fa-image text-4xl"></i>
                  </div>
                )}
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-full">
                  {story.category || 'General'}
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-bold text-lg text-foreground dark:text-white line-clamp-1 mb-1 group-hover:text-primary dark:group-hover:text-primary-400 transition-colors">{story.title}</h3>
                <p className="text-sm text-muted-foreground dark:text-gray-400 line-clamp-2 mb-4 flex-1">{story.description}</p>
                <div className="flex items-center justify-between text-xs text-muted-foreground dark:text-gray-400 font-medium">
                  <span className="flex items-center gap-1"><i className="fa fa-list-ol"></i> {story.total_episodes || 0} Ep</span>
                  <span className="flex items-center gap-1"><i className="fa fa-clock"></i> {Math.round((story.total_duration_seconds || 0) / 60)} min</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const StoryDetailScreen = () => {
  const { id } = useLocation().pathname.split('/').slice(-1);
  const [story, setStory] = useState<Story | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const { playEpisode, currentEpisode, isPlaying, togglePlay } = useContext(PlayerContext);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const [storyRes, epRes] = await Promise.all([
          apiFetch(`/data?app_id=${APP_ID}&table_name=stories&id=${id}`),
          apiFetch(`/data?app_id=${APP_ID}&table_name=episodes&story_id=${id}`)
        ]);
        if (storyRes.length > 0) setStory(storyRes[0]);
        
        const sortedEps = epRes.sort((a: Episode, b: Episode) => a.episode_number - b.episode_number);
        setEpisodes(sortedEps);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center dark:bg-gray-900"><i className="fa fa-spinner fa-spin text-3xl text-primary dark:text-primary-500"></i></div>;
  if (!story) return <div className="min-h-screen flex items-center justify-center dark:bg-gray-900 dark:text-white">Story not found</div>;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 animate-fade-in pb-32 md:pb-24">
      <div className="flex flex-col md:flex-row gap-8 mb-12">
        <div className="w-full md:w-1/3 lg:w-1/4 shrink-0">
          <div className="aspect-square rounded-2xl overflow-hidden shadow-xl border border-border dark:border-gray-700 bg-muted dark:bg-gray-800">
            {story.cover_image_url ? (
              <img src={getFullImageUrl(story.cover_image_url)} alt={story.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><i className="fa fa-image text-4xl text-muted-foreground dark:text-gray-500"></i></div>
            )}
          </div>
        </div>
        
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-primary/10 dark:bg-primary-900/30 text-primary dark:text-primary-400 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              {story.category || 'General'}
            </span>
            {story.is_premium && (
              <span className="bg-warning/10 dark:bg-yellow-900/30 text-warning dark:text-yellow-500 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                <i className="fa fa-crown"></i> Premium
              </span>
            )}
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-foreground dark:text-white mb-4 leading-tight">{story.title}</h1>
          <p className="text-lg text-muted-foreground dark:text-gray-300 mb-6 leading-relaxed">{story.description}</p>
          
          <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-foreground dark:text-gray-300 mb-8">
            <div className="flex items-center gap-2 bg-surface dark:bg-gray-800 px-4 py-2 rounded-lg border border-border dark:border-gray-700 shadow-sm">
              <i className="fa fa-list-ol text-primary dark:text-primary-400"></i> {episodes.length} Episodes
            </div>
            <div className="flex items-center gap-2 bg-surface dark:bg-gray-800 px-4 py-2 rounded-lg border border-border dark:border-gray-700 shadow-sm">
              <i className="fa fa-globe text-primary dark:text-primary-400"></i> {story.language || 'English'}
            </div>
          </div>

          {episodes.length > 0 && (
            <button 
              onClick={() => playEpisode(episodes[0], story)}
              className="w-full md:w-auto px-8 h-14 bg-gradient-warm text-white rounded-button font-bold text-lg shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3"
            >
              <i className="fa fa-play-circle text-xl"></i> Start Listening
            </button>
          )}
        </div>
      </div>

      <div className="bg-surface dark:bg-gray-800 rounded-2xl border border-border dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border dark:border-gray-700 bg-muted/30 dark:bg-gray-800/50">
          <h3 className="text-xl font-bold text-foreground dark:text-white">Episodes</h3>
        </div>
        <div className="divide-y divide-border dark:divide-gray-700">
          {episodes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground dark:text-gray-400">No episodes available yet.</div>
          ) : (
            episodes.map((ep) => {
              const isThisPlaying = currentEpisode?.id === ep.id;
              return (
                <div key={ep.id} className={`p-4 sm:p-6 flex items-center gap-4 hover:bg-muted/20 dark:hover:bg-gray-700/30 transition-colors ${isThisPlaying ? 'bg-primary/5 dark:bg-primary-900/20' : ''}`}>
                  <div className="w-12 h-12 shrink-0 rounded-full bg-muted dark:bg-gray-700 flex items-center justify-center text-lg font-bold text-foreground dark:text-gray-300">
                    {ep.episode_number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-bold text-base sm:text-lg truncate ${isThisPlaying ? 'text-primary dark:text-primary-400' : 'text-foreground dark:text-white'}`}>
                      {ep.title}
                    </h4>
                    <p className="text-sm text-muted-foreground dark:text-gray-400 line-clamp-1">{ep.description}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-4">
                    <span className="hidden sm:block text-sm font-medium text-muted-foreground dark:text-gray-400">
                      {Math.round(ep.duration_seconds / 60)} min
                    </span>
                    <button 
                      onClick={() => isThisPlaying ? togglePlay() : playEpisode(ep, story)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-sm ${isThisPlaying ? 'bg-primary dark:bg-primary-500 text-white' : 'bg-surface dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white hover:border-primary dark:hover:border-primary-500 hover:text-primary dark:hover:text-primary-400'}`}
                    >
                      <i className={`fa ${isThisPlaying && isPlaying ? 'fa-pause' : 'fa-play'} ${!isThisPlaying || !isPlaying ? 'ml-1' : ''}`}></i>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const CreatorDashboard = () => {
  const { user } = useContext(AuthContext);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchMyStories = async () => {
      try {
        const res = await apiFetch(`/data?app_id=${APP_ID}&table_name=stories&user_id=${user.id}`);
        setStories(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMyStories();
  }, [user]);

  if (!user) return <div className="p-8 text-center dark:text-white">Please login to access Studio.</div>;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 animate-fade-in pb-24 md:pb-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground dark:text-white">Creator Studio</h1>
          <p className="text-muted-foreground dark:text-gray-400">Manage your audio stories and episodes.</p>
        </div>
        <Link to="/creator/new" className="h-12 px-6 bg-gradient-warm text-white rounded-button font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2">
          <i className="fa fa-plus"></i> Create New Story
        </Link>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-24 bg-surface dark:bg-gray-800 rounded-xl animate-pulse border border-border dark:border-gray-700"></div>)}
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-20 bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700 border-dashed">
          <div className="w-20 h-20 bg-primary/10 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fa fa-microphone text-3xl text-primary dark:text-primary-400"></i>
          </div>
          <h3 className="text-xl font-bold text-foreground dark:text-white">Your studio is empty</h3>
          <p className="text-muted-foreground dark:text-gray-400 mt-2 mb-6">Start your journey as an audio creator today.</p>
          <Link to="/creator/new" className="inline-flex h-12 px-8 bg-primary dark:bg-primary-500 text-white rounded-button font-bold shadow-md hover:shadow-lg transition-all items-center justify-center">
            Create First Story
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map(story => (
            <div key={story.id} className="bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700 shadow-sm overflow-hidden flex flex-col">
              <div className="h-32 relative bg-muted dark:bg-gray-700">
                {story.cover_image_url && (
                  <img src={getFullImageUrl(story.cover_image_url)} alt="Cover" className="w-full h-full object-cover opacity-80" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4">
                  <h3 className="font-bold text-white text-lg line-clamp-1">{story.title}</h3>
                </div>
                <div className="absolute top-3 right-3">
                  <span className={`px-2 py-1 rounded text-xs font-bold ${story.is_published ? 'bg-success text-white' : 'bg-warning text-white'}`}>
                    {story.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between text-sm text-muted-foreground dark:text-gray-400 mb-4">
                  <span><i className="fa fa-list-ol mr-1"></i> {story.total_episodes || 0} Episodes</span>
                  <span><i className="fa fa-tag mr-1"></i> {story.category || 'None'}</span>
                </div>
                <div className="mt-auto flex gap-2">
                  <Link to={`/creator/story/${story.id}`} className="flex-1 h-10 bg-primary/10 dark:bg-primary-900/30 text-primary dark:text-primary-400 rounded-lg font-semibold flex items-center justify-center hover:bg-primary/20 dark:hover:bg-primary-900/50 transition-colors">
                    Manage Episodes
                  </Link>
                  <Link to={`/creator/edit/${story.id}`} className="w-10 h-10 bg-surface dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg flex items-center justify-center hover:bg-muted dark:hover:bg-gray-600 transition-colors">
                    <i className="fa fa-cog"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StoryEditor = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit/');
  const storyId = isEdit ? location.pathname.split('/').pop() : null;

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{msg: string, type: 'success'|'error'} | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Fiction',
    language: 'English',
    is_published: false,
    cover_image_url: ''
  });

  useEffect(() => {
    if (isEdit && storyId) {
      apiFetch(`/data?app_id=${APP_ID}&table_name=stories&id=${storyId}`).then(res => {
        if (res.length > 0) {
          const s = res[0];
          setFormData({
            title: s.title || '',
            description: s.description || '',
            category: s.category || 'Fiction',
            language: s.language || 'English',
            is_published: s.is_published || false,
            cover_image_url: s.cover_image_url || ''
          });
        }
      });
    }
  }, [isEdit, storyId]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !user) return;
    try {
      setLoading(true);
      const url = await uploadFile(e.target.files[0], user.id);
      setFormData(prev => ({ ...prev, cover_image_url: url }));
      setToast({ msg: 'Image uploaded successfully', type: 'success' });
    } catch (err) {
      setToast({ msg: 'Image upload failed', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    try {
      const payload = {
        app_id: APP_ID,
        table_name: 'stories',
        data: {
          ...(isEdit ? { id: storyId } : {}),
          user_id: user.id,
          ...formData,
          total_episodes: isEdit ? undefined : 0,
          total_duration_seconds: isEdit ? undefined : 0
        }
      };
      
      const res = await apiFetch('/data', 'POST', payload);
      setToast({ msg: `Story ${isEdit ? 'updated' : 'created'} successfully!`, type: 'success' });
      setTimeout(() => navigate(`/creator/story/${res.id || storyId}`), 1000);
    } catch (err: any) {
      setToast({ msg: err.message || 'Failed to save story', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in pb-24 md:pb-8">
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-surface dark:bg-gray-800 border border-border dark:border-gray-700 flex items-center justify-center hover:bg-muted dark:hover:bg-gray-700 transition-colors dark:text-white">
          <i className="fa fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground dark:text-white">
          {isEdit ? 'Edit Story Settings' : 'Create New Story'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700 shadow-sm p-6 md:p-8 space-y-6">
        
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-1/3 shrink-0">
            <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-2">Cover Image</label>
            <div className="aspect-square rounded-xl border-2 border-dashed border-border dark:border-gray-600 bg-muted/50 dark:bg-gray-700/50 flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer hover:bg-muted dark:hover:bg-gray-700 transition-colors">
              {formData.cover_image_url ? (
                <>
                  <img src={getFullImageUrl(formData.cover_image_url)} alt="Cover" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white font-medium text-sm"><i className="fa fa-camera mr-2"></i>Change</span>
                  </div>
                </>
              ) : (
                <div className="text-center p-4">
                  <i className="fa fa-cloud-upload-alt text-3xl text-muted-foreground dark:text-gray-400 mb-2"></i>
                  <p className="text-sm text-muted-foreground dark:text-gray-400">Click to upload cover</p>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" disabled={loading} />
            </div>
          </div>

          <div className="flex-1 space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Story Title</label>
              <input 
                type="text" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
                className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none dark:text-white"
                placeholder="Enter an engaging title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Description</label>
              <textarea 
                required rows={4} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                className="w-full p-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none resize-none dark:text-white"
                placeholder="What is this story about?"
              ></textarea>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Category</label>
                <select 
                  value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}
                  className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none appearance-none dark:text-white"
                >
                  <option>Fiction</option>
                  <option>Non-Fiction</option>
                  <option>Fantasy</option>
                  <option>Sci-Fi</option>
                  <option>Romance</option>
                  <option>Thriller</option>
                  <option>Mystery</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Language</label>
                <select 
                  value={formData.language} onChange={e => setFormData({...formData, language: e.target.value})}
                  className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none appearance-none dark:text-white"
                >
                  <option>English</option>
                  <option>Spanish</option>
                  <option>French</option>
                  <option>German</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input 
                type="checkbox" id="publish" checked={formData.is_published} onChange={e => setFormData({...formData, is_published: e.target.checked})}
                className="w-5 h-5 rounded border-border dark:border-gray-600 text-primary dark:text-primary-500 focus:ring-primary dark:focus:ring-primary-500"
              />
              <label htmlFor="publish" className="text-sm font-medium text-foreground dark:text-gray-300 cursor-pointer">
                Publish immediately (visible to listeners)
              </label>
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-border dark:border-gray-700 flex justify-end gap-4">
          <button type="button" onClick={() => navigate(-1)} className="px-6 h-12 rounded-button font-semibold text-foreground dark:text-white hover:bg-muted dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={loading} className="px-8 h-12 bg-gradient-warm text-white rounded-button font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-70 flex items-center gap-2">
            {loading ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-save"></i> Save Story</>}
          </button>
        </div>
      </form>
    </div>
  );
};

const EpisodeManager = () => {
  const { id } = useLocation().pathname.split('/').slice(-1);
  const [story, setStory] = useState<Story | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const [storyRes, epRes] = await Promise.all([
          apiFetch(`/data?app_id=${APP_ID}&table_name=stories&id=${id}`),
          apiFetch(`/data?app_id=${APP_ID}&table_name=episodes&story_id=${id}`)
        ]);
        if (storyRes.length > 0) setStory(storyRes[0]);
        setEpisodes(epRes.sort((a: Episode, b: Episode) => a.episode_number - b.episode_number));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center dark:bg-gray-900"><i className="fa fa-spinner fa-spin text-3xl text-primary dark:text-primary-500"></i></div>;
  if (!story) return <div className="min-h-screen flex items-center justify-center dark:bg-gray-900 dark:text-white">Story not found</div>;

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 animate-fade-in pb-24 md:pb-8">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/creator')} className="w-10 h-10 rounded-full bg-surface dark:bg-gray-800 border border-border dark:border-gray-700 flex items-center justify-center hover:bg-muted dark:hover:bg-gray-700 transition-colors dark:text-white">
          <i className="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground dark:text-white">Manage Episodes</h1>
          <p className="text-muted-foreground dark:text-gray-400">{story.title}</p>
        </div>
      </div>

      <div className="bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-border dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-xl font-bold text-foreground dark:text-white">Episode List ({episodes.length})</h3>
          <Link to={`/creator/episode/new/${story.id}`} className="h-10 px-6 bg-primary dark:bg-primary-500 text-white rounded-lg font-bold shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2">
            <i className="fa fa-plus"></i> Add Episode
          </Link>
        </div>
        
        <div className="divide-y divide-border dark:divide-gray-700">
          {episodes.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-muted dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fa fa-headphones text-2xl text-muted-foreground dark:text-gray-400"></i>
              </div>
              <h4 className="text-lg font-bold text-foreground dark:text-white">No episodes yet</h4>
              <p className="text-muted-foreground dark:text-gray-400 mt-1">Add your first episode to start building your story.</p>
            </div>
          ) : (
            episodes.map(ep => (
              <div key={ep.id} className="p-4 sm:p-6 flex items-center gap-4 hover:bg-muted/20 dark:hover:bg-gray-700/30 transition-colors">
                <div className="w-12 h-12 shrink-0 rounded-lg bg-muted dark:bg-gray-700 flex items-center justify-center text-lg font-bold text-foreground dark:text-gray-300">
                  {ep.episode_number}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-base sm:text-lg text-foreground dark:text-white truncate">{ep.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground dark:text-gray-400 mt-1">
                    <span><i className="fa fa-clock mr-1"></i> {Math.round(ep.duration_seconds / 60)} min</span>
                    <span className={`px-2 py-0.5 rounded font-bold ${ep.is_published ? 'bg-success/10 text-success dark:bg-green-900/30 dark:text-green-400' : 'bg-warning/10 text-warning dark:bg-yellow-900/30 dark:text-yellow-500'}`}>
                      {ep.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div className="shrink-0">
                  <button className="w-10 h-10 rounded-lg border border-border dark:border-gray-600 text-foreground dark:text-white hover:bg-muted dark:hover:bg-gray-700 transition-colors flex items-center justify-center">
                    <i className="fa fa-edit"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const EpisodeEditor = () => {
  const { storyId } = useLocation().pathname.match(/\/creator\/episode\/new\/(?<storyId>[^/]+)/)?.groups || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState<{msg: string, type: 'success'|'error'} | null>(null);
  
  const [formData, setFormData] = useState({
    episode_number: 1,
    title: '',
    description: '',
    transcript: '',
    audio_url: '',
    is_published: false
  });

  useEffect(() => {
    if (storyId) {
      apiFetch(`/data?app_id=${APP_ID}&table_name=episodes&story_id=${storyId}`).then(res => {
        if (res.length > 0) {
          const maxNum = Math.max(...res.map((e: Episode) => e.episode_number));
          setFormData(prev => ({ ...prev, episode_number: maxNum + 1 }));
        }
      });
    }
  }, [storyId]);

  const handleGenerateAudio = async () => {
    if (!formData.transcript.trim()) {
      setToast({ msg: 'Please enter a transcript to generate audio', type: 'error' });
      return;
    }
    setGenerating(true);
    try {
      const url = await generateTTS(formData.transcript);
      setFormData(prev => ({ ...prev, audio_url: url }));
      setToast({ msg: 'Audio generated successfully!', type: 'success' });
    } catch (err: any) {
      setToast({ msg: err.message || 'Failed to generate audio', type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.audio_url) {
      setToast({ msg: 'Please generate or upload audio first', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/data', 'POST', {
        app_id: APP_ID,
        table_name: 'episodes',
        data: {
          story_id: storyId,
          ...formData,
          duration_seconds: 300
        }
      });
      setToast({ msg: 'Episode created successfully!', type: 'success' });
      setTimeout(() => navigate(`/creator/story/${storyId}`), 1000);
    } catch (err: any) {
      setToast({ msg: err.message || 'Failed to save episode', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in pb-24 md:pb-8">
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-surface dark:bg-gray-800 border border-border dark:border-gray-700 flex items-center justify-center hover:bg-muted dark:hover:bg-gray-700 transition-colors dark:text-white">
          <i className="fa fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground dark:text-white">Add New Episode</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700 shadow-sm p-6 md:p-8 space-y-6">
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Ep Number</label>
            <input 
              type="number" required min="1" value={formData.episode_number} onChange={e => setFormData({...formData, episode_number: parseInt(e.target.value)})}
              className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none dark:text-white"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Episode Title</label>
            <input 
              type="text" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full h-12 px-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none dark:text-white"
              placeholder="e.g. Chapter 1: The Beginning"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Short Description</label>
          <textarea 
            required rows={2} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
            className="w-full p-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none resize-none dark:text-white"
            placeholder="Brief summary of this episode"
          ></textarea>
        </div>

        <div className="border-t border-border dark:border-gray-700 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-foreground dark:text-white">Audio Content</h3>
            <span className="text-xs font-medium bg-primary/10 dark:bg-primary-900/30 text-primary dark:text-primary-400 px-2 py-1 rounded flex items-center gap-1">
              <i className="fa fa-magic"></i> AI Powered
            </span>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-gray-300 mb-1">Story Text (Transcript)</label>
              <textarea 
                rows={6} value={formData.transcript} onChange={e => setFormData({...formData, transcript: e.target.value})}
                className="w-full p-4 rounded-lg bg-input dark:bg-gray-700 border border-border dark:border-gray-600 focus:ring-2 focus:ring-primary dark:focus:ring-primary-500 outline-none resize-none dark:text-white"
                placeholder="Paste your story text here to generate AI audio..."
              ></textarea>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <button 
                type="button" onClick={handleGenerateAudio} disabled={generating || !formData.transcript.trim()}
                className="w-full sm:w-auto px-6 h-12 bg-secondary dark:bg-secondary-600 text-white rounded-lg font-bold shadow-sm hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generating ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-wave-square"></i>}
                {generating ? 'Generating Audio...' : 'Generate AI Audio'}
              </button>
              
              {formData.audio_url && (
                <div className="flex-1 w-full flex items-center gap-3 bg-success/10 dark:bg-green-900/20 border border-success/20 dark:border-green-800 p-3 rounded-lg text-success dark:text-green-400 text-sm font-medium">
                  <i className="fa fa-check-circle text-lg"></i> Audio ready!
                  <audio src={getFullImageUrl(formData.audio_url)} controls className="h-8 ml-auto max-w-[200px]" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-border dark:border-gray-700">
          <input 
            type="checkbox" id="publish_ep" checked={formData.is_published} onChange={e => setFormData({...formData, is_published: e.target.checked})}
            className="w-5 h-5 rounded border-border dark:border-gray-600 text-primary dark:text-primary-500 focus:ring-primary dark:focus:ring-primary-500"
          />
          <label htmlFor="publish_ep" className="text-sm font-medium text-foreground dark:text-gray-300 cursor-pointer">
            Publish episode immediately
          </label>
        </div>

        <div className="pt-6 flex justify-end gap-4">
          <button type="button" onClick={() => navigate(-1)} className="px-6 h-12 rounded-button font-semibold text-foreground dark:text-white hover:bg-muted dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={loading || !formData.audio_url} className="px-8 h-12 bg-gradient-warm text-white rounded-button font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-70 flex items-center gap-2">
            {loading ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-save"></i> Save Episode</>}
          </button>
        </div>
      </form>
    </div>
  );
};

const LibraryScreen = () => {
  const { user } = useContext(AuthContext);
  
  if (!user) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 dark:bg-gray-900">
      <i className="fa fa-bookmark text-5xl text-muted-foreground dark:text-gray-500 mb-4"></i>
      <h2 className="text-2xl font-bold text-foreground dark:text-white mb-2">Your Library</h2>
      <p className="text-muted-foreground dark:text-gray-400 mb-6 text-center">Sign in to save stories and track your listening progress.</p>
      <Link to="/login" className="px-8 h-12 bg-primary dark:bg-primary-500 text-white rounded-button font-bold flex items-center justify-center shadow-md">
        Sign In
      </Link>
    </div>
  );

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8 animate-fade-in pb-24 md:pb-8">
      <h1 className="text-3xl font-bold text-foreground dark:text-white mb-2">My Library</h1>
      <p className="text-muted-foreground dark:text-gray-400 mb-8">Stories you've saved and your listening history.</p>
      
      <div className="text-center py-20 bg-surface dark:bg-gray-800 rounded-card border border-border dark:border-gray-700 border-dashed">
        <i className="fa fa-headphones text-4xl text-muted-foreground dark:text-gray-500 mb-4"></i>
        <h3 className="text-xl font-bold text-foreground dark:text-white">Library is empty</h3>
        <p className="text-muted-foreground dark:text-gray-400 mt-2 mb-6">Start exploring to add stories to your library.</p>
        <Link to="/" className="inline-flex h-12 px-8 bg-primary dark:bg-primary-500 text-white rounded-button font-bold shadow-md hover:shadow-lg transition-all items-center justify-center">
          Discover Stories
        </Link>
      </div>
    </div>
  );
};

// --- Main App Component ---

export default function App() {
  useEffect(() => {
    try {
      analyticsService.initialize();
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
    }
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const [currentStory, setCurrentStory] = useState<Story | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user_data');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch (e) {}
    }
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    localStorage.setItem('user_data', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user_data');
  };

  const playEpisode = (episode: Episode, story: Story) => {
    if (currentEpisode?.id === episode.id) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentEpisode(episode);
      setCurrentStory(story);
      setIsPlaying(true);
    }
  };

  const togglePlay = () => setIsPlaying(!isPlaying);
  const closePlayer = () => {
    setCurrentEpisode(null);
    setCurrentStory(null);
    setIsPlaying(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <PlayerContext.Provider value={{ currentEpisode, currentStory, isPlaying, playEpisode, togglePlay, closePlayer }}>
        <div className="min-h-screen bg-background dark:bg-gray-900 text-foreground dark:text-white font-sans flex flex-col">
          <Navbar />
          <main className="flex-1 relative">
            <Routes>
              <Route path="/" element={<HomeScreen />} />
              <Route path="/login" element={<AuthScreen />} />
              <Route path="/story/:id" element={<StoryDetailScreen />} />
              <Route path="/library" element={<LibraryScreen />} />
              <Route path="/creator" element={<CreatorDashboard />} />
              <Route path="/creator/new" element={<StoryEditor />} />
              <Route path="/creator/edit/:id" element={<StoryEditor />} />
              <Route path="/creator/story/:id" element={<EpisodeManager />} />
              <Route path="/creator/episode/new/:storyId" element={<EpisodeEditor />} />
            </Routes>
          </main>
          <GlobalPlayer />
        </div>
      </PlayerContext.Provider>
    </AuthContext.Provider>
  );
}